import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthStore } from '../../store/authStore'
import { useToastStore } from '../../store/toastStore'
import { useFeatureFlags } from '../../hooks/shared/useFeatureFlags'
import { translateApiError } from '../../lib/apiErrors'
import DocumentAcceptance, { DocumentAcceptanceData } from '../../components/auth/DocumentAcceptance'
import MarketingConsent, { MarketingConsentState } from '../../components/marketing/MarketingConsent'
import PhoneInput from '../../components/ui/PhoneInput'
import ReferralCodeInput, { getStoredReferralCode, setStoredReferralCode } from '../../components/referrals/ReferralCodeInput'
import HeardAboutField from '../../components/auth/HeardAboutField'
import { HEARD_ABOUT_OTHER } from '../../lib/heardAbout'
import { AttributionTrackingService } from '../../services/attribution'
import Tooltip from '../../components/ui/Tooltip'
import { documentPdfUrl } from '../../lib/documentPdfUrl'
import AuthBrand from '../../components/auth/AuthBrand'
import AuthShell from '../../components/auth/AuthShell'
import {
  authErrorTextClass,
  authFootnoteClass,
  authHeadingClass,
  authInputClass,
  authInputErrorClass,
  authLabelClass,
  authLinkClass,
  authSectionDividerClass,
  authSubtextClass,
  authSubmitClass,
} from '../../components/auth/authTheme'

interface FormData {
  first_name: string
  last_name: string
  email: string
  password: string
  confirmPassword: string
}

interface FormErrors {
  [key: string]: string
}

export default function Register() {
  const [formData, setFormData] = useState<FormData>({
    first_name: '',
    last_name: '',
    email: '',
    password: '',
    confirmPassword: '',
  })
  const [errors, setErrors] = useState<FormErrors>({})
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [acceptedDocuments, setAcceptedDocuments] = useState<DocumentAcceptanceData[]>([])
  const [allDocumentsAccepted, setAllDocumentsAccepted] = useState(false)
  const [phoneNumber, setPhoneNumber] = useState('')
  const [referralCode, setReferralCode] = useState(getStoredReferralCode)
  const [heardAbout, setHeardAbout] = useState('')
  const [heardAboutDetail, setHeardAboutDetail] = useState('')
  const [marketingConsents, setMarketingConsents] = useState<MarketingConsentState>({
    email_marketing: false,
    phone_marketing: false,
  })

  const { t, i18n } = useTranslation(['auth', 'common'])
  const { register, isLoading } = useAuthStore()
  const { addToast } = useToastStore()
  const { isGoogleSSOEnabled } = useFeatureFlags()

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!formData.first_name.trim()) {
      newErrors.first_name = t('auth:register.validation.firstNameRequired')
    }

    if (!formData.last_name.trim()) {
      newErrors.last_name = t('auth:register.validation.lastNameRequired')
    }

    if (!formData.email.trim()) {
      newErrors.email = t('common:form.emailRequired')
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('common:form.emailInvalidFormat')
    }

    if (!formData.password) {
      newErrors.password = t('common:form.passwordRequired')
    } else if (formData.password.length < 8) {
      newErrors.password = t('common:form.passwordTooShort')
    }

    if (!formData.confirmPassword) {
      newErrors.confirmPassword = t('auth:register.validation.confirmPasswordRequired')
    } else     if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = t('common:form.passwordsMismatch')
    }

    if (!heardAbout) {
      newErrors.heard_about = t('auth:register.validation.heardAboutRequired')
    } else if (heardAbout === HEARD_ABOUT_OTHER && !heardAboutDetail.trim()) {
      newErrors.heard_about_detail = t('auth:register.validation.heardAboutOtherRequired')
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleDocumentAcceptanceChange = (acceptances: DocumentAcceptanceData[], allAccepted: boolean) => {
    setAcceptedDocuments(acceptances)
    setAllDocumentsAccepted(allAccepted)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    if (!allDocumentsAccepted) {
      addToast(t('common:form.documentsRequired'), 'error')
      return
    }

    try {
      await register({
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        password: formData.password,
        language: i18n.language,
        phone_number: phoneNumber || undefined,
        accepted_documents: acceptedDocuments,
        marketing_consents: marketingConsents,
        referral_code: referralCode.trim() || undefined,
        attribution: AttributionTrackingService.getAttribution() ?? undefined,
        heard_about: heardAbout,
        heard_about_detail: heardAbout === HEARD_ABOUT_OTHER ? heardAboutDetail.trim() : undefined,
      })

      setStoredReferralCode('')

      setIsSubmitted(true)
      addToast(t('auth:register.success'), 'success')
    } catch (error) {
      addToast(translateApiError(error, t), 'error')
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))

    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }))
    }
  }

  if (isSubmitted) {
    return (
      <AuthShell>
        <div className="text-center">
          <AuthBrand />
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-900/40 border border-green-700">
            <svg className="h-6 w-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className={`mt-6 ${authHeadingClass}`}>
            {t('auth:register.checkEmail.heading')}
          </h2>
          <p className="mt-2 text-sm text-gray-400">
            {t('auth:register.checkEmail.sentTo')}{' '}
            <span className="font-medium text-white">{formData.email}</span>
          </p>
          <p className="mt-4 text-sm text-gray-400">
            {t('auth:register.checkEmail.instruction')}
          </p>
          <div className="mt-6">
            <Link to="/auth/login" className={authLinkClass}>
              {t('common:backToLogin')}
            </Link>
          </div>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell wide>
      <div>
        <AuthBrand />
        <h2 className={authHeadingClass}>{t('auth:register.heading')}</h2>
        <p className={authSubtextClass}>
          {t('auth:register.orPrompt')}{' '}
          <Link to="/auth/login" className={authLinkClass}>
            {t('auth:register.loginLink')}
          </Link>
        </p>
      </div>

      <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
        <div className="space-y-4">
          <div className="flex space-x-4">
            <div className="flex-1">
              <label htmlFor="first_name" className={authLabelClass}>
                {t('common:form.firstNameLabel')}
              </label>
              <input
                id="first_name"
                name="first_name"
                type="text"
                required
                className={`mt-1 rounded-md ${authInputClass} ${
                  errors.first_name ? authInputErrorClass : ''
                }`}
                placeholder={t('common:form.firstNameLabel')}
                value={formData.first_name}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              {errors.first_name && <p className={authErrorTextClass}>{errors.first_name}</p>}
            </div>

            <div className="flex-1">
              <label htmlFor="last_name" className={authLabelClass}>
                {t('common:form.lastNameLabel')}
              </label>
              <input
                id="last_name"
                name="last_name"
                type="text"
                required
                className={`mt-1 rounded-md ${authInputClass} ${
                  errors.last_name ? authInputErrorClass : ''
                }`}
                placeholder={t('common:form.lastNameLabel')}
                value={formData.last_name}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              {errors.last_name && <p className={authErrorTextClass}>{errors.last_name}</p>}
            </div>
          </div>

          <div>
            <label htmlFor="email" className={authLabelClass}>
              {t('common:form.emailLabel')}
            </label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="email"
              required
              className={`mt-1 rounded-md ${authInputClass} ${
                errors.email ? authInputErrorClass : ''
              }`}
              placeholder={t('common:form.emailLabel')}
              value={formData.email}
              onChange={handleInputChange}
              disabled={isLoading}
            />
            {errors.email && <p className={authErrorTextClass}>{errors.email}</p>}
          </div>

          <div>
            <div className="flex items-center gap-1.5 mb-1">
              <label className={authLabelClass}>{t('common:form.phoneLabel')}</label>
              <Tooltip content={t('common:form.phoneTooltip')} position="right" multiline>
                <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-gray-600 text-gray-300 text-xs cursor-default select-none leading-none">
                  ?
                </span>
              </Tooltip>
              <span className="text-xs text-gray-500 ml-1">{t('common:form.optional')}</span>
            </div>
            <PhoneInput
              value={phoneNumber}
              onChange={setPhoneNumber}
              disabled={isLoading}
              variant="dark"
            />
          </div>

          <ReferralCodeInput
            value={referralCode}
            onChange={setReferralCode}
            disabled={isLoading}
            variant="dark"
          />

          <HeardAboutField
            value={heardAbout}
            detail={heardAboutDetail}
            onChange={(value) => {
              setHeardAbout(value)
              if (errors.heard_about) setErrors((prev) => ({ ...prev, heard_about: '' }))
            }}
            onDetailChange={(value) => {
              setHeardAboutDetail(value)
              if (errors.heard_about_detail) setErrors((prev) => ({ ...prev, heard_about_detail: '' }))
            }}
            disabled={isLoading}
            error={errors.heard_about}
            detailError={errors.heard_about_detail}
            label={t('auth:register.heardAbout.label')}
            placeholder={t('auth:register.heardAbout.placeholder')}
            detailPlaceholder={t('auth:register.heardAbout.otherPlaceholder')}
            optionLabel={(option) => t(`auth:register.heardAbout.options.${option}`)}
          />

          <div>
            <label htmlFor="password" className={authLabelClass}>
              {t('common:form.passwordLabel')}
            </label>
            <div className="relative mt-1">
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                className={`rounded-md pr-10 ${authInputClass} ${
                  errors.password ? authInputErrorClass : ''
                }`}
                placeholder={t('auth:register.passwordPlaceholder')}
                value={formData.password}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-200"
                onClick={() => setShowPassword((v) => !v)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.password && <p className={authErrorTextClass}>{errors.password}</p>}
          </div>

          <div>
            <label htmlFor="confirmPassword" className={authLabelClass}>
              {t('common:form.confirmPasswordLabel')}
            </label>
            <div className="relative mt-1">
              <input
                id="confirmPassword"
                name="confirmPassword"
                type={showConfirmPassword ? 'text' : 'password'}
                autoComplete="new-password"
                required
                className={`rounded-md pr-10 ${authInputClass} ${
                  errors.confirmPassword ? authInputErrorClass : ''
                }`}
                placeholder={t('common:form.confirmPasswordLabel')}
                value={formData.confirmPassword}
                onChange={handleInputChange}
                disabled={isLoading}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-200"
                onClick={() => setShowConfirmPassword((v) => !v)}
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
              >
                {showConfirmPassword ? (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                ) : (
                  <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className={authErrorTextClass}>{errors.confirmPassword}</p>
            )}
          </div>
        </div>

        <div className={authSectionDividerClass}>
          <DocumentAcceptance
            onChange={handleDocumentAcceptanceChange}
            disabled={isLoading}
            variant="dark"
          />
        </div>

        <div className={authSectionDividerClass}>
          <MarketingConsent
            value={marketingConsents}
            onChange={setMarketingConsents}
            disabled={isLoading}
            variant="dark"
          />
        </div>

        <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
          <a
            href={documentPdfUrl('polityka_prywatnosci', i18n.language)}
            target="_blank"
            rel="noopener noreferrer"
            className={authFootnoteClass}
          >
            {t('auth:marketingConsent.informationalDocs.privacyPolicy')}
          </a>
          <a
            href={documentPdfUrl('odstapienie_formularz', i18n.language)}
            target="_blank"
            rel="noopener noreferrer"
            className={authFootnoteClass}
          >
            {t('auth:marketingConsent.informationalDocs.withdrawalForm')}
          </a>
        </div>

        <div>
          <button
            type="submit"
            disabled={isLoading || !allDocumentsAccepted}
            className={authSubmitClass}
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#0d0d0d]" />
            ) : (
              t('auth:register.submitButton')
            )}
          </button>
        </div>

        {isGoogleSSOEnabled() && (
          <div className="text-center">
            <p className="text-xs text-gray-500">
              {t('auth:register.googleHint')}{' '}
              <Link to="/auth/login" className={authLinkClass}>
                {t('auth:register.googleHintLink')}
              </Link>
            </p>
          </div>
        )}
      </form>
    </AuthShell>
  )
}
